import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { FaAngleLeft, FaAngleRight } from 'react-icons/fa';

import Button, { ButtonAppearance } from '../../Atoms/Button';

import { useSize } from '../../../shared/hooks/useSize';

import * as S from './CardCarousel.styles';

export const CardCarousel = ({
  children,
  title,
  buttonLabel,
  buttonLink,
  isGallery,
}) => {
  if (!children || children.length < 3) {
    return null;
  }

  // Used to calculate how much item have to be displayed and to prevent over scroll.
  const screenWidth = useSize().width;
  let displayedItems = 3;
  if (screenWidth <= 1360) {
    displayedItems = 2;
  }

  if (screenWidth <= 450) {
    displayedItems = 1;
  }

  const hasNavigation = !isGallery && children.length > 3;
  const lastIndex = !isGallery && children.length > 3 ? 5 : 3;
  const displayedChildren = children.slice(0, lastIndex);
  const isDisplayedAsGallery = isGallery || displayedChildren.length <= 3;

  const [currentIndex, setCurrentIndex] = useState(0);
  const [numberOfChildren, setNumberOfChildren] = useState(children.length);
  const isLastItem = !(currentIndex < numberOfChildren - displayedItems);

  useEffect(() => {
    setNumberOfChildren(displayedChildren.length);
  }, [displayedChildren]);

  const next = () => {
    if (!isLastItem) {
      setCurrentIndex(prevState => prevState + 1);
    }
  };

  const prev = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prevState => prevState - 1);
    }
  };

  return (
    <S.Carousel>
      <S.Head
        displayedItems={displayedItems}
        isDisplayedAsGallery={isDisplayedAsGallery}
      >
        {title && <S.Title>{title}</S.Title>}
        <S.ButtonWrapper
          displayedItems={displayedItems}
          hasNavigation={hasNavigation}
        >
          <Button
            altText='previous-arrow'
            appearance={ButtonAppearance.CIRCULAR_FLOATING}
            isDisabled={currentIndex <= 0}
            onClick={() => prev()}
          >
            <FaAngleLeft />
          </Button>
          <Button
            altText='next-arrow'
            appearance={ButtonAppearance.CIRCULAR_FLOATING}
            isDisabled={isLastItem}
            onClick={() => next()}
          >
            <FaAngleRight />
          </Button>
        </S.ButtonWrapper>
      </S.Head>
      <S.ContentContainer isDisplayedAsGallery={isDisplayedAsGallery}>
        <S.Content>
          <S.ListContainer>
            <S.List
              currentIndex={currentIndex}
              displayedItems={displayedItems}
              isDisplayedAsGallery={isDisplayedAsGallery}
            >
              {React.Children.map(displayedChildren, (child, index) => (
                <S.Item
                  isDisplayedAsGallery={isDisplayedAsGallery}
                  key={`${child}-${index}`}
                >
                  {child}
                </S.Item>
              ))}
            </S.List>
          </S.ListContainer>
        </S.Content>
      </S.ContentContainer>
      {buttonLabel && buttonLink && (
        <S.ButtonContainer>
          <Button link={buttonLink}>{buttonLabel}</Button>
        </S.ButtonContainer>
      )}
    </S.Carousel>
  );
};

CardCarousel.propTypes = {
  /**
   * Button label. Need also the setted buttonLink
   * to be displayed.
   * (optional)
   */
  buttonLabel: PropTypes.string,
  /**
   * Button link. Need also the setted buttonLabel
   * to be displayed.
   * (optional)
   */
  buttonLink: PropTypes.string,
  /**
   * List of articles (3 minimum) including categroy color
   * and categroy name, illustration url and illustration alt,
   * lede, a list of tags, article's title and article's url.
   */
  children: PropTypes.node.isRequired,
  /**
   * If true, display only 3 articles maximum and hides arrows controllers
   * on wide screen. If false, displays 5 articles maximum and
   * the arrows controllers.
   * (default = true)
   */
  isGallery: PropTypes.bool,
  /**
   * Section title.
   * (optional)
   */
  title: PropTypes.string,
};

CardCarousel.defaultProps = {
  isGallery: true,
};
